import Bugsnag from "@bugsnag/js";
import { createCipheriv, createDecipheriv, randomBytes } from "crypto";
import dayjs from "dayjs";
import { get, push, ref, set, update } from "firebase/database";
import MixPanel from "mixpanel-browser";
import { renderToString } from "react-dom/server";
import { ServerStyleSheet } from "styled-components";

import axiosInstance from "./axiosInstance";
import { FIREDB } from "./firebaseInstance";
import { addToast } from "./Store/Actions";
import { store } from "./Store/configureStore";
import URL from "./urls";
import {
  CF_PROVIDER_ROLE,
  apiURL,
  indianTimezones,
  providerLabels,
  scullyURL,
  TYPE_PSYCHIATRIST,
} from "./variable";

let pendingSessionToJoin = null;
if (typeof window !== "undefined" && window.ReactNativeWebView) {
  window.addEventListener("message", event => {
    const { type, token } = event.data || {};
    if (type === "MIC_PERMISSION") {
      if (token) {
        if (pendingSessionToJoin) {
          performRedirect(pendingSessionToJoin);
          pendingSessionToJoin = null;
        }
      } else {
        if (typeof store !== "undefined" && store.dispatch) {
          store.dispatch(
            addToast({
              msg: "Microphone permission is required to continue.",
              autoDismiss: true,
              appearance: "error",
              showBottomToastOnMobile: true,
            })
          );
        }
        pendingSessionToJoin = null;
      }
    }
  });
}

//analytics
export const trackEvent = ({ event, payload }) => {
  const currentUser = store?.getState()?.auth?.user;
  const providerProfile = store?.getState()?.provider?.providerProfile;

  const isCfProvider = providerProfile?.roles?.some(role =>
    CF_PROVIDER_ROLE.includes(role)
  );

  const updatedPayload = {
    provider_name: currentUser
      ? `${currentUser.firstname} ${currentUser.lastname}`
      : "null",
    provider_id: currentUser?.id ?? "null",
    provider_type: currentUser?.usertype ?? "null",
    provider_uuid: currentUser?.uuid ?? "null",
    is_cf_provider: isCfProvider ?? "null",
    platform: localStorage.getItem("platform"),
    ...payload,
  };

  if (import.meta.env.DEV) {
    console.log("TRACKER", { event, payload: updatedPayload });
  }

  MixPanel.track(event, updatedPayload);
};

// Log to Firebase Realtime Database
export const logToFirebase = async step => {
  try {
    const searchParams = new URLSearchParams(window.location.search);

    if (!searchParams.get("logEvents")) return;

    const logsRef = ref(FIREDB, "twaLogs");

    await push(logsRef, {
      timestamp: Date.now(),
      step,
    });
  } catch (error) {
    Bugsnag.notify(error, event => {
      event.addMetadata("firebase_logging", {
        message: "Failed to log to Firebase",
        step,
      });
    });
  }
};

export const decryptMessage = (encryptedMessage, decryptionKey) => {
  let textArr = encryptedMessage.split("");
  const iv = textArr.splice(0, 32).join("");
  const ct = textArr.join("");

  let encryptedText = Buffer.from(ct, "hex");

  let decipher = createDecipheriv(
    "aes-256-cbc",
    decryptionKey,
    Buffer.from(iv, "hex")
  );
  let decrypted = decipher.update(encryptedText);

  decrypted = Buffer.concat([decrypted, decipher.final()]);
  return decrypted.toString();
};

export const encryptMessage = (text, decryptionKey) => {
  // generate iv byte array
  const iv = randomBytes(16);

  let cipher = createCipheriv("aes-256-cbc", decryptionKey, iv);
  let encrypted = cipher.update(text);
  encrypted = Buffer.concat([encrypted, cipher.final()]);

  return iv.toString("hex").concat(encrypted.toString("hex"));
};

export const redirectToTherapyRoom = session => {
  if (window.ReactNativeWebView) {
    pendingSessionToJoin = session;
    window.ReactNativeWebView.postMessage("checkAndPromptForPermissions");
    return;
  }
  performRedirect(session);
};

export const generateAccessToken = async () => {
  try {
    const response = await axiosInstance.get(URL.giveaccesstoken);
    return response?.data?.token;
  } catch (error) {
    Bugsnag.notify(error);
    return {};
  }
};

export const performRedirect = session => {
  axiosInstance
    .get(`${URL.sessionDetails}/${session.id}`)
    .then(async res => {
      const sessionDetail = res.data.booking;

      if (sessionDetail?.status !== "booked") {
        alert(
          `Cannot join session. Session status is: "${sessionDetail?.status}"`
        );
        window.location.reload();
        return;
      }

      if (sessionDetail?.session_started_at === null) {
        await axiosInstance
          .post(`${URL.providerSessionStarted}/${session.id}/startsession`, {
            bookingid: session.id,
          })
          .catch(err => Bugsnag.notify(err));
      }
      const token = await generateAccessToken();
      const clientUuid = session?.customer?.uuid;
      const bookingId = session?.id;
      if (clientUuid && bookingId) {
        token.redirectUrl = window.encodeURIComponent(
          `${window.location.origin}/dashboard/${session?.customer?.uuid}/booking/${session?.id}`
        );
      } else {
        // Fallback
        token.redirectUrl = window.encodeURIComponent(window.location.href);
      }

      const tokenStr = JSON.stringify(token);
      const serialtoken = Buffer.from(tokenStr).toString("base64");

      if (!serialtoken) {
        Bugsnag.notify("TokenError: Serial token is null or undefined");
      }
      const platform = window.ReactNativeWebView ? "app" : "website";

      const joinSessionUrl = `${scullyURL}/${sessionDetail.id}/${serialtoken}/${platform}`;

      // Check if mobile view (consistent with useMobileView hook logic)
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      if (isMobile) {
        window.location.href = joinSessionUrl;
      } else {
        window.open(joinSessionUrl, "_blank");
      }
    })
    .catch(err => {
      alert("Error fetching session details");
      Bugsnag.notify(err);
    });
};

export const openLink = url => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(`openLink,${url}`);
  } else window.open(url);
};

// Sends Suggest Provider Prompt in chat
export const sendSppInChat = async (
  clientFirebaseId,
  providerFirebaseId,
  providerName,
  sppPayload
) => {
  if (!clientFirebaseId || !providerFirebaseId || !providerName || !sppPayload)
    return;

  try {
    let channelKey;
    const chatChannel = [clientFirebaseId, providerFirebaseId].sort().join("-");
    const time_stamp = parseInt(Date.now() / 1000);

    const keyRes = await axiosInstance.post(URL.getChannelKey, {
      channel: chatChannel,
    });
    channelKey = keyRes.data?.key;

    const payload = {
      send_by_user_id: providerFirebaseId,
      send_to_user_id: clientFirebaseId,
      message: encryptMessage(JSON.stringify(sppPayload), channelKey),
      message_type: "spp",
      user_name: providerName,
      time_stamp: time_stamp,
      source: "website",
      encrypted: true,
    };

    const tempData = {
      lastmodified: time_stamp,
      info: { name: payload.user_name },
    };

    const user_friend_map = `user_friend_map/${payload.send_to_user_id}/${payload.send_by_user_id}`;

    update(ref(FIREDB, user_friend_map), tempData);

    set(ref(FIREDB, `channels/${chatChannel}/lastmodified`), time_stamp);

    push(ref(FIREDB, `${user_friend_map}/unread_messages`), payload);

    push(ref(FIREDB, `channels/${chatChannel}/messages`), payload);
  } catch (err) {
    Bugsnag.notify(err);
  }
};

// Sends NSP in chat
export const sendNspInChat = async (
  clientFirebaseId,
  providerFirebaseId,
  providerName,
  message_type,
  nspPayload
) => {
  if (!clientFirebaseId || !providerFirebaseId || !providerName || !nspPayload)
    return;
  try {
    let channelKey;
    const chatChannel = [clientFirebaseId, providerFirebaseId].sort().join("-");
    const time_stamp = parseInt(Date.now() / 1000);

    const keyRes = await axiosInstance.post(URL.getChannelKey, {
      channel: chatChannel,
    });
    channelKey = keyRes.data?.key;

    const payload = {
      send_by_user_id: providerFirebaseId,
      send_to_user_id: clientFirebaseId,
      message: encryptMessage(JSON.stringify(nspPayload), channelKey),
      message_type,
      user_name: providerName,
      time_stamp: time_stamp,
      source: "website",
      encrypted: true,
    };

    const tempData = {
      lastmodified: time_stamp,
      info: { name: payload.user_name },
    };

    const user_friend_map = `user_friend_map/${payload.send_to_user_id}/${payload.send_by_user_id}`;

    update(ref(FIREDB, user_friend_map), tempData);

    set(ref(FIREDB, `channels/${chatChannel}/lastmodified`), time_stamp);

    push(ref(FIREDB, `${user_friend_map}/unread_messages`), payload);

    push(ref(FIREDB, `channels/${chatChannel}/messages`), payload);
  } catch (err) {
    Bugsnag.notify(err);
  }
};

export const sendNewMessageInChat = async ({
  roomId,
  content,
  system_event,
}) => {
  if (!roomId || !content) return;

  const payload = {
    content,
    client_message_uuid:
      window.crypto?.randomUUID?.() || randomBytes(16).toString("hex"),
    timestamp: Date.now(),
    reply_to_id: null,
  };

  if (system_event) {
    payload.system_event = system_event;
  }

  try {
    await axiosInstance.post(
      `${apiURL}/chat/rooms/${roomId}/messages`,
      payload
    );
  } catch (err) {
    Bugsnag.notify(err);
  }
};

// Sends message in chat
export const sendMessageInChat = async (
  clientFirebaseId,
  providerFirebaseId,
  providerName,
  message,
  messageType
) => {
  if (
    !clientFirebaseId ||
    !providerFirebaseId ||
    !providerName ||
    !message ||
    !messageType
  )
    return;
  try {
    let channelKey;
    const chatChannel = [clientFirebaseId, providerFirebaseId].sort().join("-");
    const time_stamp = parseInt(Date.now() / 1000);

    const keyRes = await axiosInstance.post(URL.getChannelKey, {
      channel: chatChannel,
    });
    channelKey = keyRes.data?.key;

    const payload = {
      send_by_user_id: providerFirebaseId,
      send_to_user_id: clientFirebaseId,
      message: encryptMessage(message, channelKey),
      message_type: messageType,
      user_name: providerName,
      time_stamp: time_stamp,
      source: "website",
      encrypted: true,
    };

    const tempData = {
      lastmodified: time_stamp,
      info: { name: payload.user_name },
    };

    const user_friend_map = `user_friend_map/${payload.send_to_user_id}/${payload.send_by_user_id}`;

    update(ref(FIREDB, user_friend_map), tempData);

    set(ref(FIREDB, `channels/${chatChannel}/lastmodified`), time_stamp);

    push(ref(FIREDB, `${user_friend_map}/unread_messages`), payload);

    push(ref(FIREDB, `channels/${chatChannel}/messages`), payload);
  } catch (err) {
    Bugsnag.notify(err);
  }
};

export const getProviderStatus = async providerFirebaseId => {
  if (!providerFirebaseId) return;
  try {
    const snapshot = await get(ref(FIREDB, `Status/${providerFirebaseId}`));
    const currentProvideStatus = snapshot.val();
    return currentProvideStatus;
  } catch (err) {
    Bugsnag.notify(err);
    return null;
  }
};

export const setProviderStatus = async (providerFirebaseId, status) => {
  if (!providerFirebaseId || !status) return;
  try {
    await set(ref(FIREDB, `Status/${providerFirebaseId}`), status);
  } catch (err) {
    Bugsnag.notify(err);
    return null;
  }
};

export const handleTypingStatusOnAuth = async providerFirebaseId => {
  /*
  Checks if the status is typing, if it is then set to offline by default
  Runs:- 
    - on login
    - on logout
  */
  try {
    const status = await getProviderStatus(providerFirebaseId);
    if (status && status?.toLowerCase() === "typing") {
      setProviderStatus("Offline");
    }
  } catch (err) {
    Bugsnag.notify(err);
    console.log({ err });
  }
};

export const blockedMobileEvents = [
  "NOTIFICATION_EVENT_NAME_PROVIDER_TAGGING_ADD_ALERT",
  "NOTIFICATION_EVENT_NAME_PROVIDER_TAGGING_CHANGE_ALERT",
  "NOTIFICATION_EVENT_NAME_PROVIDER_TAGGING_TERMINATE_ALERT",
  "NOTIFICATION_EVENT_NAME_SUPERVISOR_PROVIDER_TAGGING_ADD_ALERT",
  "NOTIFICATION_EVENT_NAME_SUPERVISOR_PROVIDER_TAGGING_CHANGE_ALERT",
  "NOTIFICATION_EVENT_NAME_SUPERVISOR_PROVIDER_TAGGING_TERMINATE_ALERT",
  "NOTIFICATION_EVENT_NAME_NEW_CLIENTS_COUNT_UPDATE",
  "NOTIFICATION_EVENT_NAME_MONTHLY_COMPLETD_SESSION_COUNT_REMINDER",
];

export const getNotificationAction = (
  isMobile,
  navigate,
  dispatch,
  { event, booking_slotdatetime, client_details },
  userType,
  routedFromApp = false,
  room_id
) => {
  if (isMobile && blockedMobileEvents.includes(event?.event_type)) {
    dispatch(
      addToast({
        msg: "To complete this action, please use a laptop or desktop.",
        autoDismiss: true,
        showBottomToastOnMobile: true,
      })
    );
    if (routedFromApp) navigate("/dashboard");
    return;
  }

  const { id: clientId, firebaseid, uuid } = client_details || {};

  switch (event?.event_type) {
    case "NOTIFICATION_EVENT_NAME_BOOKING_ACTION_REMINDER":
    case "NOTIFICATION_EVENT_NAME_NEW_CLIENT_BOOKING_BOOKED_ALERT":
    case "NOTIFICATION_EVENT_NAME_BOOKING_BOOKED_ALERT":
    case "NOTIFICATION_EVENT_NAME_BOOKING_RESCHEDULED_ALERT":
      if (booking_slotdatetime)
        navigate(
          `/dashboard?date=${dayjs(booking_slotdatetime).format("YYYY-MM-DD")}`
        );
      break;

    case "NOTIFICATION_EVENT_NAME_BOOKING_CANCELLED_ALERT":
      navigate(`/dashboard/sessions?tab=cancelled`);
      break;

    case "NOTIFICATION_EVENT_NAME_CLIENT_CHAT_REMINDER":
      if (firebaseid && uuid)
        navigate(
          `/clients/${firebaseid}?client_uuid=${uuid}&showChat=1&room_id=${room_id}`
        );
      break;

    case "NOTIFICATION_EVENT_NAME_BSE_TOOLS_REMINDER":
    case "NOTIFICATION_EVENT_NAME_CLIENT_ASSESSMENT_REMINDER":
      if (firebaseid && uuid)
        navigate(
          `/clients/${firebaseid}?client_uuid=${uuid}&currentTab=allTools`
        );
      break;

    case "NOTIFICATION_EVENT_NAME_BOOKING_NOTES_REMINDER":
      navigate(`/clients?pending_tasks=notes_pending&client_status=active`);
      break;

    case "NOTIFICATION_EVENT_NAME_CLIENT_MGAF_REMINDER":
      if (userType) {
        const pendingTask = userType === TYPE_PSYCHIATRIST ? "cgi" : "mgaf";
        navigate(
          `/clients?pending_tasks=${pendingTask}_pending&client_status=active`
        );
      }
      break;

    case "NOTIFICATION_EVENT_NAME_CLIENT_TREATMENT_PLAN_REMINDER":
      navigate(
        `/clients?pending_tasks=treatment_plan_pending&client_status=active`
      );
      break;

    case "NOTIFICATION_EVENT_NAME_ACTIVE_CLIENT_BOOKING_ABSENT_ALERT":
      if (firebaseid && uuid)
        navigate(
          `/clients/${firebaseid}?client_uuid=${uuid}&currentTab=sessions`
        );
      break;

    case "NOTIFICATION_EVENT_NAME_ADD_CONCERNS_TO_B2B_CLIENTS_ALERT":
      navigate(`/clients?client_type=b2b`);
      break;

    case "NOTIFICATION_EVENT_NAME_PROVIDER_TAGGING_ADD_ALERT":
    case "NOTIFICATION_EVENT_NAME_PROVIDER_TAGGING_CHANGE_ALERT":
      if (firebaseid && uuid && clientId)
        navigate(
          `/clients/${firebaseid}?client_uuid=${uuid}&clientId=${clientId}&showUpdateProviderModal=1&currentScreen=updateHistory`
        );
      break;

    case "NOTIFICATION_EVENT_NAME_PROVIDER_TAGGING_TERMINATE_ALERT":
      break;

    case "NOTIFICATION_EVENT_NAME_SUPERVISOR_PROVIDER_TAGGING_ADD_ALERT":
    case "NOTIFICATION_EVENT_NAME_SUPERVISOR_PROVIDER_TAGGING_CHANGE_ALERT":
    case "NOTIFICATION_EVENT_NAME_SUPERVISOR_PROVIDER_TAGGING_TERMINATE_ALERT":
      navigate("/supervision");
      break;

    case "NOTIFICATION_EVENT_NAME_NEW_CLIENTS_COUNT_UPDATE":
    case "NOTIFICATION_EVENT_NAME_MONTHLY_COMPLETD_SESSION_COUNT_REMINDER":
      navigate("/analytics");
      break;

    case "NOTIFICATION_EVENT_NAME_COMPLETED_SESSIONS_WITH_CLIENT_UPDATE":
      if (firebaseid && uuid)
        navigate(
          `/clients/${firebaseid}?client_uuid=${uuid}&currentTab=sessions&sessionsFilterOption=completed`
        );
      break;

    default:
      if (routedFromApp) navigate("/dashboard");
      break;
  }
};

const isMobileAndroidBrowser =
  /Android/i.test(navigator.userAgent) && /Mobile/i.test(navigator.userAgent);

const isMobileIOSBrowser =
  /iPhone|iPad|iPod/i.test(navigator.userAgent) &&
  /Mobile/i.test(navigator.userAgent);

export const getPlatformType = (isMobile, platformQuery) => {
  if (!isMobile) {
    return "desktop_browser";
  } else if (platformQuery === "android") {
    return "mobile_android_app";
  } else if (platformQuery === "ios") {
    return "mobile_ios_app";
  } else if (isMobileAndroidBrowser) {
    return "mobile_android_browser";
  } else if (isMobileIOSBrowser) {
    return "mobile_ios_browser";
  } else {
    return "unknown";
  }
};

export const encodeBase64Html = html => {
  try {
    return Buffer.from(html).toString("base64");
  } catch (error) {
    Bugsnag.notify(error);
  }
};

export const decodeBase64Html = html => {
  try {
    return Buffer.from(html, "base64").toString("utf-8");
  } catch (error) {
    Bugsnag.notify(error);
  }
};

export const generateHtmlWithStyles = element => {
  const sheet = new ServerStyleSheet();

  const html = renderToString(sheet.collectStyles(element));

  const styleTags = sheet.getStyleTags();

  const fullHtml = `
    <html>
      <head>
        <style>
          @import url("https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap");
          * {
            font-family: "Quicksand", sans-serif;
          }
        </style>
        ${styleTags}
      </head>
      <body style="margin: 0; ">
        <div id="app">${html}</div>
      </body>
    </html>
  `;

  return fullHtml;
};

// Get default selection for filter options from query params
export const getDefaultSelection = (filterOptions, filterOption) => {
  return (
    filterOptions.find(option => option.value === filterOption) ||
    filterOptions[0]
  );
};

export const getNotificationActionType = title => {
  let type = null;
  switch (title?.toLowerCase()) {
    case "approval":
      type = "binary_action";
      break;
    case "bse":
      type = "redirection";
      break;
    case "bse tools":
      type = "redirection";
      break;
    case "session":
      type = "info";
      break;
    case "client":
      type = "redirection";
      break;
    default:
      type = "info";
      break;
  }
  return type;
};

export const getFormattedDate = (date, format = "D MMM YYYY, h:mm A") =>
  date ? dayjs(date).format(format) : null;

export const isToday = date => {
  return dayjs(date).isSame(dayjs(), "day");
};

export const isYesterday = date => {
  return dayjs(date).isSame(dayjs().subtract(1, "day"), "day");
};

export const isValidEmail = email => {
  if (!email) return false;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

export const calculateAge = dateOfBirth => {
  if (!dateOfBirth) return null;
  const today = dayjs();
  const birthDate = dayjs(dateOfBirth);
  return today.diff(birthDate, "year");
};

export const getSearchParams = params => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value != null)
  );
  return new URLSearchParams(filteredParams).toString();
};

export const getLatestAvaialableDate = sessionSchedules => {
  if (sessionSchedules.length === 0) return;

  let latestAvailableDate = dayjs();
  const latestAvailableSchedule = sessionSchedules.find(
    schedule => schedule.available_slots > 0
  );
  if (latestAvailableSchedule) {
    const latestScheduleTime = latestAvailableSchedule.epoch_time;
    latestAvailableDate = dayjs(latestScheduleTime);
  }

  return latestAvailableDate.format("YYYY-MM-DD");
};

export const isInternationalTimezone = timezone => {
  return !!timezone && !indianTimezones.includes(timezone);
};

export const convertToTimezone = (datetime, timezone) => {
  try {
    return dayjs(datetime).tz(timezone);
  } catch {
    Bugsnag.notify(`Invalid timezone: ${timezone}`);
    return dayjs(datetime);
  }
};

export const formatDatetimeWithTimezone = (
  datetime,
  timezone,
  displayTimezone,
  format = "DD MMM, h:mm A "
) => {
  if (!datetime || !timezone) return null;

  let formattedDateTime;

  // use displayTimezone as fallback if shortName is missing
  const shortTimezoneName =
    displayTimezone?.split?.(" - ")?.[1] || displayTimezone;

  formattedDateTime =
    convertToTimezone(datetime, timezone).format(format) + shortTimezoneName;

  return formattedDateTime;
};

export const getServiceLabel = providerType => {
  if (!providerType) return "";

  const providerLabel = providerLabels[providerType];

  return providerLabel ? providerLabel.replace("ist", "y") : "";
};

export {
  CT_FIELD_GROUPS,
  CT_RESPONSE_TO_INPUT_KEY_MAP,
  CT_PARTNER_RESPONSE_TO_INPUT_KEY_MAP,
  buildCouplePayloadDetails,
  createEhrPayload,
  mapCtPartnerDetailsToInputData,
} from "./utils/ctEhrHelper";
